import { useEffect, useMemo, useState } from 'react'
import {
  confirmVerification,
  loadVerification,
  type VerificationSubject,
} from '@core/verification'
import { loadThumbnail } from '@core/attachments'
import { moveToTrash } from '@core/trash'
import { validateIban, formatIban } from '@core/validators/iban'
import { Button, Card, ErrorNote, Spinner } from '@design/components'
import { DocumentViewer } from '@features/documents/DocumentViewer'
import { ProvenanceImage } from './ProvenanceImage'
import { FieldRow } from './FieldRow'
import { RoutingPicker } from './RoutingPicker'
import type { Space } from '@core/session'
import { Overlay, SheetOverlay } from '@design/Overlay'

/**
 * Amber-lane verification: the document above, the fields below, and a box
 * drawn wherever you tap.
 *
 * Target is under eight seconds for a clean invoice. Everything here serves
 * that: the fields are ordered by what matters for paying, the primary button
 * is one tap, and nothing asks for a decision that the risk engine could have
 * made on its own.
 */
export function VerifyScreen({
  documentId,
  userId,
  spaces,
  onDone,
  onClose,
}: {
  documentId: string
  userId: string
  spaces: Space[]
  onDone: () => void
  onClose: () => void
}) {
  const [subject, setSubject] = useState<VerificationSubject | null | undefined>(undefined)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [corrections, setCorrections] = useState<Record<string, string>>({})
  const [active, setActive] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [routing, setRouting] = useState<{ spaceId: string; category: string | null } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [fullScreen, setFullScreen] = useState(false)
  // Acknowledging a changed IBAN is its own decision, deliberately separate
  // from the extracted fields — it is a statement about a phone call the human
  // made, not about what the document says.
  const [ibanChangeAcknowledged, setIbanChangeAcknowledged] = useState(false)
  const [discarding, setDiscarding] = useState(false)

  useEffect(() => {
    let alive = true
    void loadVerification(documentId)
      .then((s) => alive && setSubject(s))
      .catch((e) => alive && (setSubject(null), setError(String(e?.message ?? e))))
    void loadThumbnail(documentId).then(
      (t) => alive && setImageUrl(t && t.contentType !== 'application/pdf' ? t.url : null),
    )
    return () => {
      alive = false
    }
  }, [documentId])

  const valueOf = (name: string) =>
    corrections[name] ?? subject?.fields.find((f) => f.name === name)?.value ?? ''

  const activeBbox = useMemo(() => {
    const f = subject?.fields.find((x) => x.name === active)
    return f?.bbox ?? null
  }, [active, subject])

  const ibanWarning = useMemo(() => {
    if (!subject) return null
    const iban = validateIban(valueOf('iban'))
    if (!iban.valid) return null
    if (subject.knownIbans.length === 0) return null
    if (subject.knownIbans.includes(iban.canonical)) {
      return { kind: 'match' as const, count: subject.knownIbans.length }
    }
    return { kind: 'changed' as const, previous: subject.knownIbans[0] ?? '' }
  }, [subject, corrections])

  // Inside the Overlay like every other branch. Returned bare, it rendered
  // inside whatever list opened this screen — a spinner floating in the middle
  // of the inbox rather than on the screen that is loading.
  if (subject === undefined) {
    return (
      <Overlay>
        <Spinner label="Even geduld…" />
      </Overlay>
    )
  }

  // Nothing was extracted: either the pipeline has not reached it yet, or it
  // gave up (rule 18's dead-letter). This used to be a dead end — a sentence
  // and a back button — which is the worst possible place for one. It is the
  // screen that runs on handwritten invoices and faded thermal receipts, the
  // spec says this path must be pleasant (§1118), and since Incoming now only
  // clears what has been acknowledged, a document with no way out of here
  // would sit in the inbox for good.
  //
  // So: look at the paper yourself, or throw it away. Both are real answers.
  if (subject === null) {
    return (
      <Overlay className="pad-safe-top pad-safe-bottom px-4">
        <button
          onClick={onClose}
          className="self-start min-h-[2.75rem] -ml-2 px-2 text-muted"
        >
          ← Terug
        </button>
        <Card className="mb-3">
          <p className="font-medium mb-1">Ik kon dit niet lezen</p>
          <p className="text-sm text-muted">
            Misschien is het nog niet verwerkt, misschien was de foto te
            onduidelijk. Bekijk het zelf — of gooi het weg als er niets mee
            hoeft te gebeuren.
          </p>
          <ErrorNote>{error}</ErrorNote>
        </Card>

        <div className="mt-auto space-y-2">
          <Button size="large" onClick={() => setFullScreen(true)}>
            Bekijk het origineel
          </Button>
          <Button tone="quiet" onClick={() => setDiscarding(true)}>
            Weggooien
          </Button>
        </div>

        {discarding && (
          <DiscardSheet
            busy={saving}
            onCancel={() => setDiscarding(false)}
            onConfirm={async () => {
              setSaving(true)
              try {
                await moveToTrash('document', documentId)
                onDone()
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e))
                setSaving(false)
                setDiscarding(false)
              }
            }}
          />
        )}

        {fullScreen && (
          <DocumentViewer documentId={documentId} onClose={() => setFullScreen(false)} />
        )}
      </Overlay>
    )
  }

  const blocked = ibanWarning?.kind === 'changed' && !ibanChangeAcknowledged

  return (
    <Overlay>
      {/* Both edges of this bar used to be a problem. The back button was the
          height of its own text — about 30px, under Apple's 44px minimum — and
          the right edge was an empty spacer where the one missing action
          belonged. */}
      <header className="shrink-0 pad-safe-top px-2 pb-2 flex items-center justify-between gap-2">
        <button
          onClick={onClose}
          aria-label="Terug"
          className="min-h-[2.75rem] px-3 -ml-1 text-muted font-medium"
        >
          ← Terug
        </button>
        <span className="text-sm font-medium truncate">
          {subject.supplierName ?? 'Controleren'}
        </span>
        <button
          onClick={() => setDiscarding(true)}
          aria-label="Weggooien"
          className="min-h-[2.75rem] px-3 -mr-1 text-muted"
        >
          Weggooien
        </button>
      </header>

      {/* Document above, fields below. The split is deliberate: you should be
          able to see the paper and the number at the same time. */}
      <div className="h-[38vh] shrink-0 border-b border-line">
        <ProvenanceImage
          src={imageUrl}
          bbox={activeBbox}
          onOpenFull={() => setFullScreen(true)}
        />
      </div>

      <div className="flex-1 scroll-area px-3 py-3">
        {ibanWarning?.kind === 'changed' && (
          <Card className="mb-3 border-danger/40 bg-danger/5">
            <p className="font-medium mb-1">Rekeningnummer is veranderd</p>
            <p className="text-sm text-muted mb-2">
              Deze leverancier gebruikte eerder {formatIban(ibanWarning.previous)}.
            </p>
            <p className="text-sm">
              Bel de leverancier op een nummer dat je al had — niet het nummer op deze factuur.
            </p>
          </Card>
        )}

        {ibanWarning?.kind === 'match' && (
          <p className="text-sm text-ok mb-2 px-1">
            ✓ Zelfde rekeningnummer als eerder bij deze leverancier
          </p>
        )}

        <RoutingPicker
          spaces={spaces}
          spaceId={routing?.spaceId ?? subject.spaceId}
          category={routing?.category ?? subject.category ?? null}
          onSpace={(id) =>
            setRouting((r) => ({
              spaceId: id,
              // Moving between Huis and Zaak changes which categories exist,
              // so a category chosen for the other space is cleared rather
              // than silently kept as something that is no longer offered.
              category: id === (r?.spaceId ?? subject.spaceId) ? (r?.category ?? subject.category ?? null) : null,
            }))
          }
          onCategory={(c) =>
            setRouting((r) => ({ spaceId: r?.spaceId ?? subject.spaceId, category: c }))
          }
        />

        <div className="space-y-1">
          {subject.fields.map((field) => (
            <FieldRow
              key={field.name}
              field={field}
              value={valueOf(field.name)}
              active={active === field.name}
              editing={editing === field.name}
              onSelect={() => {
                setActive(field.name)
                setEditing(null)
              }}
              onEdit={() => {
                setActive(field.name)
                setEditing(field.name)
              }}
              onChange={(v) => setCorrections((c) => ({ ...c, [field.name]: v }))}
            />
          ))}
        </div>

        <ErrorNote>{error}</ErrorNote>
      </div>

      <div className="shrink-0 px-4 pb-4 pt-2 pad-safe-bottom border-t border-line">
        <Button
          size="large"
          disabled={saving || blocked}
          onClick={async () => {
            setSaving(true)
            setError(null)
            try {
              await confirmVerification({
                subject,
                corrections,
                userId,
                ibanChangeAcknowledged,
                routing: routing ?? undefined,
              })
              onDone()
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e))
              setSaving(false)
            }
          }}
        >
          {saving ? 'Bezig…' : blocked ? 'Eerst rekeningnummer controleren' : 'Klopt — nagekeken'}
        </Button>

        {ibanWarning?.kind === 'changed' && !ibanChangeAcknowledged && (
          <button
            onClick={() => setIbanChangeAcknowledged(true)}
            className="w-full mt-2 py-3 text-sm text-muted"
          >
            Ik heb gebeld — het nieuwe nummer klopt
          </button>
        )}
      </div>

      {/* Not every photo is a bill. A parent sends a duplicate, a photo of the
          cat, the back of an envelope — and until now the only way past it was
          to verify it as if it were real. Two taps, and it lands in the trash
          where it can be fetched back for thirty days. */}
      {discarding && (
        <DiscardSheet
          busy={saving}
          onCancel={() => setDiscarding(false)}
          onConfirm={async () => {
            setSaving(true)
            try {
              await moveToTrash('document', documentId)
              onDone()
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e))
              setSaving(false)
              setDiscarding(false)
            }
          }}
        />
      )}

      {fullScreen && (
        <DocumentViewer
          documentId={documentId}
          title={subject.supplierName ?? undefined}
          onClose={() => setFullScreen(false)}
        />
      )}
    </Overlay>
  )
}

/**
 * Two taps to throw something away, and thirty days to change your mind.
 *
 * Shared by both branches of the screen above. It used to live only in the
 * branch that had an extraction, which meant the one document you most
 * plainly want to discard — the one nothing could read — was the one you
 * could not.
 */
function DiscardSheet({
  busy,
  onCancel,
  onConfirm,
}: {
  busy: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <SheetOverlay>
      <div className="w-full bg-canvas rounded-t-2xl p-4 pad-safe-bottom">
        <p className="font-medium mb-1">Dit weggooien?</p>
        <p className="text-sm text-muted mb-4">
          Het verdwijnt uit je lijst. Je kan het 30 dagen terughalen uit de
          prullenbak.
        </p>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 min-h-[3rem] rounded-xl border border-line font-medium"
          >
            Toch niet
          </button>
          <button
            disabled={busy}
            onClick={onConfirm}
            className="flex-1 min-h-[3rem] rounded-xl border border-ink bg-ink/5 font-medium"
          >
            Weggooien
          </button>
        </div>
      </div>
    </SheetOverlay>
  )
}
